import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
  Paper,
  Input,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Psychology as PsychologyIcon,
  Error as ErrorIcon,
  UploadFile as UploadFileIcon,
  Pages as PagesIcon
} from '@mui/icons-material';
import TyphoonOCRService from '../api/TyphoonOCRService';

const PDFAnalysisComponent = ({ googleDriveUrl, requestId, title }) => {
  const [extractedText, setExtractedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState('all');
  const [ocrResults, setOcrResults] = useState([]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setError(null);
        setTotalPages(0);
        setSelectedPage('all');
        setExtractedText('');
        setOcrResults([]);
      } else {
        setError('กรุณาเลือกไฟล์ PDF เท่านั้น');
        setSelectedFile(null);
      }
    }
  };

  const handleOCR = async () => {
    if (!selectedFile) {
      setError('กรุณาเลือกไฟล์ PDF ก่อน');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);
    setExtractedText('');

    try {
      setProgress(25);
      
      console.log('Processing file:', selectedFile.name, 'Size:', selectedFile.size);
      console.log('Selected page:', selectedPage);
      
      setProgress(50);
      
      // Call Typhoon OCR API
      const ocrService = new TyphoonOCRService();
      const result = await ocrService.extractTextFromFile(selectedFile, selectedPage);
      
      setProgress(100);
      
      // Handle result based on whether it's single page or all pages
      if (typeof result === 'string') {
        setExtractedText(result);
      } else if (result.pages) {
        setOcrResults(result.pages);
        setTotalPages(result.total_pages || result.pages.length);
        
        if (selectedPage === 'all') {
          // Combine all pages
          const combinedText = result.pages.map((page, index) => 
            `=== หน้าที่ ${index + 1} ===\n${page.text}\n`
          ).join('\n');
          setExtractedText(combinedText);
        } else {
          // Show selected page only
          const pageIndex = parseInt(selectedPage) - 1;
          if (result.pages[pageIndex]) {
            setExtractedText(result.pages[pageIndex].text);
          }
        }
      } else {
        setExtractedText(result);
      }
      
    } catch (err) {
      console.error('OCR Error:', err);
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const renderExtractedText = () => {
    if (!extractedText) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mb: 2 }}>
          � ข้อความที่สกัดจากเอกสาร
        </Typography>
        
        <Paper sx={{ 
          p: 3, 
          bgcolor: '#f8f9fa', 
          maxHeight: 400, 
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: 2
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              fontFamily: 'monospace',
              fontSize: '0.9rem'
            }}
          >
            {extractedText}
          </Typography>
        </Paper>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            📊 สถิติ: ตัวอักษรทั้งหมด {extractedText.length} ตัว | 
            คำทั้งหมด {extractedText.split(/\s+/).filter(word => word.length > 0).length} คำ
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PsychologyIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="h6" component="h2">
            🤖 Typhoon OCR - PDF Text Extraction
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          อัพโหลดไฟล์ PDF เพื่อสกัดข้อความด้วย Typhoon OCR
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* File Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            เลือกไฟล์ PDF
          </Typography>
          <Input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            sx={{ width: '100%', mb: 2 }}
          />
          
          {selectedFile && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                ไฟล์ที่เลือก: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Alert>
              
              {/* Page Selection */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>เลือกหน้าที่ต้องการ OCR</InputLabel>
                  <Select
                    value={selectedPage}
                    label="เลือกหน้าที่ต้องการ OCR"
                    onChange={(e) => setSelectedPage(e.target.value)}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PagesIcon sx={{ mr: 1 }} />
                        ทุกหน้า (หน้า 1-5)
                      </Box>
                    </MenuItem>
                    {[...Array(10)].map((_, index) => (
                      <MenuItem key={index + 1} value={(index + 1).toString()}>
                        หน้าที่ {index + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {totalPages > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={`PDF มี ${totalPages} หน้า`} 
                      color="info" 
                      size="small"
                      icon={<PagesIcon />}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={handleOCR}
          disabled={loading || !selectedFile}
          startIcon={loading ? <CircularProgress size={20} /> : <AnalyticsIcon />}
          fullWidth
          sx={{ mb: 2 }}
        >
          {loading ? 'กำลังประมวลผล...' : 'สกัดข้อความด้วย Typhoon OCR'}
        </Button>

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
              {progress < 50 && 'กำลังเตรียมไฟล์...'}
              {progress >= 50 && progress < 100 && 'กำลังส่งไฟล์ไปยัง Typhoon OCR...'}
              {progress >= 100 && 'เสร็จสิ้น!'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>เกิดข้อผิดพลาด:</strong> {error}
            </Typography>
          </Alert>
        )}

        {renderExtractedText()}

        {!selectedFile && (
          <Alert severity="info">
            <strong>🔄 Typhoon OCR:</strong> กรุณาเลือกไฟล์ PDF เพื่อสกัดข้อความ
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default PDFAnalysisComponent;
